import { formatBookingCurrency } from '../../utils/bookingFormat';
import { formatSize, formatLength } from '../../constants/serviceOptions';
import type { ServiceCostLine } from '../../hooks/usePublicBookingSelection';

interface ServicePriceBoxProps {
	lines: ServiceCostLine[];
	/** Services only — add-ons are chosen later and quoted there. */
	total: number;
	size: string;
	length: string;
	sizeApplies: boolean;
	lengthApplies: boolean;
	groupClashes: boolean;
	/** Paid links only: the deposit the current total lands on. */
	depositDueToday?: number;
}

/**
 * The live "Service price" box on the service step (mirrors the walk-in mockup).
 * Re-renders from the hook's cost lines, so every service pick and every size or
 * length change updates the per-service figures and the total at once. Until the
 * basket is fully priced each line is a "from" price and the total reads
 * "Starting at".
 */
const ServicePriceBox = ({
	lines,
	total,
	size,
	length,
	sizeApplies,
	lengthApplies,
	groupClashes,
	depositDueToday,
}: ServicePriceBoxProps) => {
	if (lines.length === 0) return null;

	const missing: string[] = [];
	if (!groupClashes) {
		if (sizeApplies && !size) missing.push('size');
		if (lengthApplies && !length) missing.push('length');
	}
	const allPriced = lines.every(line => line.priced);

	const chosen = [
		size && sizeApplies ? `Size ${formatSize(size)}` : '',
		length && lengthApplies ? `${formatLength(length)} length` : '',
	]
		.filter(Boolean)
		.join(' · ');

	const totalLabel = !allPriced ? 'Starting at' : lines.length > 1 ? 'Services total' : 'Price';

	return (
		<div className="pb__cost" aria-live="polite">
			<div className="pb__cost-title">Service price</div>
			{lines.map(line => (
				<div key={line.id} className="pb__cost-row">
					<span className="pb__cost-name">
						{line.name}
						{line.priced && chosen && <span className="pb__cost-sub">{chosen}</span>}
					</span>
					<span className="pb__cost-price">
						{!line.priced && <span className="pb__cost-from">from</span>}
						{formatBookingCurrency(line.price)}
					</span>
				</div>
			))}
			{missing.length > 0 && (
				<div className="pb__cost-pending">
					Select a {missing.join(' and ')} to see your final price.
				</div>
			)}
			<div className="pb__cost-total">
				<span className="pb__cost-total-label">{totalLabel}</span>
				<span className="pb__cost-total-amount">{formatBookingCurrency(total)}</span>
			</div>
			{depositDueToday != null && (
				<div className="pb__cost-note">
					Deposit due today: {formatBookingCurrency(depositDueToday)} &middot; Add-ons can
					be chosen on the next step
				</div>
			)}
		</div>
	);
};

export default ServicePriceBox;
